import { useState } from "react";
import { Button } from "../../components";
import { numberFormatter } from "../../utils/financials";
import { useDispatch, useSelector } from "react-redux";
import { selectAccessToken } from "../../slices/staffSlice";
import { fetchRequests } from "../../Requests/getRequests";
import { useNavigate } from "react-router-dom";
import { show } from "../../slices/toastSlice";
import { HugeiconsIcon } from "@hugeicons/react";
import { CheckmarkCircle01Icon, Cancel01Icon } from "@hugeicons/core-free-icons";
import { cediSign } from "../../utils/constants";

function EligCheck({ status, data, loanType, closeModal }) {

    const accessToken = useSelector(selectAccessToken);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [processing, setProcessing] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(false);

    const handleRequest = async(e) => {
        e.preventDefault();
        setProcessing(true);
        if(error) setError(false);

        const url = `/loans/staff/apply/${loanType}`;
        // Do NOT manually set Content-Type — axios sets it automatically with the
        // correct multipart boundary when the request body is a FormData instance.
        const headers = { "authorization": `${accessToken}` };

        const formData = new FormData();
        for(const [key, value] of Object.entries(data?.data)) {
            if(key === "otherDocs") {
                value.forEach((item, index) => {
                    formData.append(`otherDocument_${index + 1}`, item);
                });
                continue;
            }
            formData.append(key, value);
        }

        const res = await fetchRequests(url, "post", headers, {}, formData);
        setProcessing(false);

        if(res?.code === 401) {
            sessionStorage.clear();
            dispatch(show({ type: "error", state: true, message: "Your session has expired. Please login to continue." }));
            closeModal();
            navigate("/login");
            return;
        }

        // Failure can arrive as { err } (business/validation, 400) or
        // { error } (unhandled server error, 500). Treat both as failures —
        // never fall through to the success path on an error response.
        if(res?.err || res?.error) {
            setError(res?.err || res?.error);
            return;
        }

        // A genuine success always carries a message; anything else is a
        // failed/empty response that must not be reported as success.
        if(!res?.message) {
            setError("Submission failed. Please try again.");
            return;
        }

        setSuccess(`${res.message}... Redirecting`);

        setTimeout(() => { closeModal(); }, 1300);
        setTimeout(() => { navigate("/"); }, 1400);
    };

    if(status !== "accepted") {
        return (
            <div className="flex items-start gap-3 px-4 py-3.5 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm">
                <HugeiconsIcon icon={Cancel01Icon} size={16} className="text-red-400 shrink-0 mt-0.5" />
                <span>{data?.reason}</span>
            </div>
        );
    }

    const rows = [
        { label: "Eligible Loan Amount", value: `${cediSign}${numberFormatter(data?.loanAmt)}` },
        { label: "Credit Score", value: `${data?.score}%` },
        { label: "Monthly Interest Rate", value: `${data?.interestRate}%` },
        { label: "Processing Fee (one-time)", value: `${cediSign}${numberFormatter(data?.paymentObj?.processingFee)}` },
        { label: "Insurance Fee (one-time)", value: `${cediSign}${numberFormatter(data?.paymentObj?.insuranceFee)}` },
        { label: "Amount to Receive", value: `${cediSign}${numberFormatter(data?.paymentObj?.amountGiven)}` },
        { label: "Repayment Frequency", value: data?.paymentFreq },
        { label: "Repayment Amount", value: `${data?.paymentObj?.numPayments} payments of ${cediSign}${numberFormatter(data?.paymentObj?.premium)}` },
    ];

    return (
        <div className="space-y-4">
            <div className="rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-50 dark:divide-gray-700/50">
                    <thead>
                        <tr className="bg-gray-50/60 dark:bg-gray-900/40">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Item</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Detail</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50 bg-white dark:bg-gray-800">
                        {rows.map((row, i) => (
                            <tr key={i}>
                                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{row.label}</td>
                                <td className="px-4 py-3 text-sm font-medium text-gray-800 dark:text-gray-200 tabular-nums">{row.value}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center gap-3">
                <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                    Cancel
                </button>
                <Button
                    btnText="Submit application"
                    btnClasses="form-button flex-1"
                    btnType="button"
                    btnFunction={handleRequest}
                    processing={processing}
                    disabled={false}
                />
            </div>

            {error && (
                <div className="flex items-start gap-3 px-4 py-3.5 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm">
                    <HugeiconsIcon icon={Cancel01Icon} size={16} className="text-red-400 shrink-0 mt-0.5" />
                    <span>{error}</span>
                </div>
            )}

            {success && (
                <div className="flex items-start gap-3 px-4 py-3.5 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-700 text-sm">
                    <HugeiconsIcon icon={CheckmarkCircle01Icon} size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                    <span>{success}</span>
                </div>
            )}
        </div>
    );
}

export default EligCheck;
